import {
  MAX_PROFILE_BIO_CHARS,
  MAX_PROFILE_INTERESTS,
  MAX_PROFILE_INTEREST_CHARS,
} from '../constants';

const FRONTEND_PROFILE_BLOCKLIST = [
  'nigger', 'nigga', 'niggah',
  'spic', 'spick',
  'chink', 'gook', 'nip', 'slope',
  'kike',
  'raghead', 'towelhead',
  'wetback',
  'paki',
  'coon', 'jigaboo', 'porch monkey',
  'redskin',
  'gypsy', 'gyp',
  'white trash',
  'faggot', 'fag', 'fagg',
  'dyke',
  'tranny', 'shemale',
  'queer',
  'homo',
  'sissy',
  'no homo',
  'slut', 'whore', 'hoe',
  'cunt',
  'bitch',
  'twat',
  'pussy',
  'cock',
  'dick',
  'bastard',
  'fuck', 'motherfucker', 'fucking',
  'shit',
  'piss',
  'damn', 'goddamn',
  'ass', 'asshole',
  'dickhead', 'shithead',
  'blowjob', 'cocksucker',
  'cum', 'jizz',
  'tits', 'titties',
  'retard', 'retarded', 'retart',
  'spaz', 'spastic',
  'mong', 'mongoloid',
  'lame',
  'dumb', 'idiot',
  'fatso', 'fatty',
  'ugly', 'hideous',
  'midget',
  'anorexic', 'bulimic',
  'kafir', 'kuffar',
  'infidel',
  'heathen',
  'apostate',
  'blasphemy',
  'weed', 'pot', 'marijuana',
  'coke', 'crack', 'heroin',
  'meth', 'speed',
  'lsd', 'acid',
  'ecstasy', 'molly',
  'pill', 'xanax', 'oxy',
  'gang', 'thug',
  'kill yourself', 'kys',
  'rape', 'molest',
  'die', 'murder',
  'bomb', 'terror', 'jihad',
  'n1gg3r', 'f4g', '$hit', '@ss',
] as const;

const LEET_MAP: Record<string, string> = {
  '@': 'a',
  '$': 's',
  '0': 'o',
  '1': 'i',
  '!': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
};

const deobfuscate = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .split('')
    .map((ch) => LEET_MAP[ch] ?? ch)
    .join('');

const normalize = (value: string): string =>
  deobfuscate(value)
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const includesWholeWord = (normalizedText: string, normalizedWord: string): boolean => {
  if (!normalizedText || !normalizedWord) return false;
  const paddedText = ` ${normalizedText} `;
  const paddedWord = ` ${normalizedWord} `;
  return paddedText.includes(paddedWord);
};

const includesSeparatedWord = (rawDeobfuscatedText: string, normalizedWord: string): boolean => {
  if (!rawDeobfuscatedText || !normalizedWord || normalizedWord.includes(' ')) return false;
  const chars = normalizedWord.split('').map((ch) => escapeRegex(ch));
  const pattern = `(^|[^a-z0-9])${chars.join('[^a-z0-9]*')}($|[^a-z0-9])`;
  const re = new RegExp(pattern, 'i');
  return re.test(rawDeobfuscatedText);
};

export function findProfileBlockedWord(values: Array<string | undefined | null>): string | null {
  const preparedValues = values
    .map((v) => {
      const rawDeobfuscated = deobfuscate(String(v || ''));
      const normalized = normalize(String(v || ''));
      return { rawDeobfuscated, normalized };
    })
    .filter((item) => item.rawDeobfuscated || item.normalized);

  if (preparedValues.length === 0) return null;

  for (const rawWord of FRONTEND_PROFILE_BLOCKLIST) {
    const normalizedWord = normalize(rawWord);
    if (!normalizedWord) continue;

    for (const value of preparedValues) {
      if (includesWholeWord(value.normalized, normalizedWord) || includesSeparatedWord(value.rawDeobfuscated, normalizedWord)) {
        return rawWord;
      }
    }
  }

  return null;
}

export function validateProfileFormInput(input: { bio?: string; interests?: string[] }): string | null {
  const bio = String(input.bio || '');
  const interests = Array.isArray(input.interests) ? input.interests : [];

  if (bio.length > MAX_PROFILE_BIO_CHARS) {
    return `Bio cannot exceed ${MAX_PROFILE_BIO_CHARS} characters.`;
  }

  if (interests.length > MAX_PROFILE_INTERESTS) {
    return `You can add up to ${MAX_PROFILE_INTERESTS} interests.`;
  }

  if (interests.some((item) => String(item || '').trim().length > MAX_PROFILE_INTEREST_CHARS)) {
    return `Each interest must be ${MAX_PROFILE_INTEREST_CHARS} characters or fewer.`;
  }

  const blockedInBio = findProfileBlockedWord([bio]);
  if (blockedInBio) {
    return `Bio contains a blocked word: ${blockedInBio}`;
  }

  const blockedInInterests = findProfileBlockedWord(interests);
  if (blockedInInterests) {
    return `Interests contain a blocked word: ${blockedInInterests}`;
  }

  return null;
}
